import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { BookOpen, Calendar, ArrowRight, Sparkles } from "lucide-react";
import { API_URL, BRAND, resolveAsset } from "../lib/brand";
import NewsletterSignup from "../components/NewsletterSignup";

export default function Magazine() {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [upcoming, setUpcoming] = useState(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    document.title = "Crescere con Cura — Il magazine di Teresa Vilardo";
    axios.get(`${API_URL}/magazines`).then((r) => setIssues(r.data)).finally(() => setLoading(false));
    axios.get(`${API_URL}/magazines/upcoming/next`).then((r) => setUpcoming(r.data || null)).catch(() => setUpcoming(null));
    const t = setInterval(() => setNow(Date.now()), 1000 * 60); // tick every minute
    return () => clearInterval(t);
  }, []);

  const countdown = () => {
    if (!upcoming?.publish_at) return null;
    const diff = new Date(upcoming.publish_at).getTime() - now;
    if (diff <= 0) return null;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return { days, hours, minutes };
  };
  const cd = countdown();

  // Backend sorts by publish_at DESC, so issues[0] is always the latest live issue.
  const featured = issues[0];
  const others = issues.slice(1);

  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid="magazine-page">
      {/* HERO */}
      <section className="pt-32 pb-16 px-6 md:px-10">
        <div className="max-w-[1400px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <p className="text-[10px] uppercase tracking-[0.5em] text-[#FF2768] font-semibold">— Il magazine mensile</p>
            <h1 className="font-display italic text-6xl md:text-8xl leading-[0.9] text-[#FF2768] mt-3">
              Crescere <span className="text-[#4E9EA5]">con Cura</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-[#3D2140]/80 font-editorial italic leading-relaxed">
              Sguardi, riflessioni e strumenti pratici sull'infanzia — un nuovo numero ogni mese, gratuito.
              Per genitori, tate ed educatori che vogliono accompagnare i bambini con rispetto, competenza e consapevolezza.
            </p>
          </motion.div>
        </div>
      </section>

      {loading && (
        <p className="text-center py-16 font-editorial italic text-[#3D2140]/60 text-xl">Sto caricando i numeri...</p>
      )}

      {/* FEATURED ISSUE */}
      {featured && (
        <section className="px-6 md:px-10 pb-16">
          <div className="max-w-[1400px] mx-auto">
            <motion.article
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center bg-white rounded-[40px] p-8 md:p-12 border border-[#3D2140]/8 shadow-xl"
              data-testid="magazine-featured"
            >
              <div className="lg:col-span-6 order-2 lg:order-1">
                <span className="inline-block px-3 py-1 rounded-full bg-[#FFE45C]/50 text-[#3D2140] text-[10px] uppercase tracking-widest font-bold mb-4">
                  <Sparkles size={11} className="inline mr-1" /> Ultimo numero
                </span>
                <p className="text-xs uppercase tracking-[0.4em] text-[#FF2768] font-semibold">— {featured.number}</p>
                <h2 className="font-display italic text-5xl md:text-6xl leading-[0.95] mt-2 text-[#3D2140]">
                  {featured.month_label}
                </h2>
                {featured.tagline && (
                  <p className="mt-4 text-2xl md:text-3xl font-editorial italic text-[#FF2768] leading-tight">
                    "{featured.tagline}"
                  </p>
                )}
                {featured.description && (
                  <p className="mt-6 text-[#3D2140]/75 leading-relaxed text-lg">
                    {featured.description}
                  </p>
                )}
                <div className="mt-8 flex flex-wrap gap-3 items-center">
                  <Link
                    to={`/magazine/${featured.slug}`}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FF2768] text-white font-semibold uppercase tracking-wider text-sm hover:bg-[#E01F5A] hover:gap-3 transition-all shadow-lg"
                    data-testid="magazine-read-btn"
                  >
                    <BookOpen size={16} /> Sfoglia il numero <ArrowRight size={16} />
                  </Link>
                  <p className="text-sm text-[#3D2140]/50">{featured.pages?.length || 0} pagine · gratuito</p>
                </div>
              </div>
              <div className="lg:col-span-6 order-1 lg:order-2">
                <Link to={`/magazine/${featured.slug}`} className="block group">
                  <div className="relative rounded-3xl overflow-hidden shadow-2xl transform hover:-rotate-1 hover:scale-[1.02] transition-transform duration-500">
                    <img
                      src={resolveAsset(featured.cover_image)}
                      alt={`Copertina ${featured.number} — ${featured.month_label}`}
                      className="w-full h-auto"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#FF2768]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </Link>
              </div>
            </motion.article>
          </div>
        </section>
      )}

      {/* ARCHIVIO */}
      {others.length > 0 && (
        <section className="px-6 md:px-10 pb-16">
          <div className="max-w-[1400px] mx-auto">
            <div className="mb-8">
              <p className="text-[10px] uppercase tracking-[0.4em] text-[#4E9EA5] font-semibold">— Archivio</p>
              <h3 className="font-display italic text-4xl md:text-5xl text-[#3D2140] mt-2">I numeri precedenti</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {others.map((iss) => (
                <Link
                  key={iss.id}
                  to={`/magazine/${iss.slug}`}
                  className="group rounded-2xl overflow-hidden bg-white border border-[#3D2140]/8 hover:border-[#FF2768] transition-colors"
                  data-testid={`magazine-issue-${iss.slug}`}
                >
                  <div className="aspect-[3/4] overflow-hidden">
                    <img src={resolveAsset(iss.cover_image)} alt={iss.month_label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-4">
                    <p className="text-[10px] uppercase tracking-widest text-[#FF2768] font-semibold">{iss.number}</p>
                    <p className="font-display text-lg mt-1 text-[#3D2140]">{iss.month_label}</p>
                    {iss.tagline && <p className="text-xs text-[#3D2140]/60 mt-1 italic line-clamp-2">"{iss.tagline}"</p>}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* EMPTY STATE / UPCOMING TEASER */}
      {!loading && issues.length === 0 && (
        <section className="px-6 md:px-10 pb-24" data-testid="magazine-empty-state">
          {upcoming ? (
            <div className="max-w-4xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center bg-white rounded-[32px] p-8 md:p-12 border border-[#3D2140]/8 shadow-xl"
              >
                <div className="relative aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl bg-[#FAF8F5]">
                  <img
                    src={resolveAsset(upcoming.cover_image)}
                    alt={upcoming.month_label}
                    className="w-full h-full object-cover"
                    data-testid="upcoming-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
                  <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FFE45C] text-[#3D2140] text-[10px] uppercase tracking-widest font-bold shadow-md">
                    <Sparkles size={11} /> In arrivo
                  </span>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] font-semibold">— {upcoming.number} · in arrivo</p>
                  <h2 className="font-display italic text-5xl md:text-6xl text-[#3D2140] mt-2 leading-[0.95]">
                    {upcoming.month_label}
                  </h2>
                  {upcoming.tagline && (
                    <p className="mt-4 text-2xl font-editorial italic text-[#FF2768] leading-tight">
                      "{upcoming.tagline}"
                    </p>
                  )}
                  <div className="mt-6 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#81C3C9]/15 border border-[#81C3C9]/30">
                    <Calendar size={16} className="text-[#4E9EA5]" />
                    <p className="text-sm text-[#3D2140] font-semibold" data-testid="upcoming-date">
                      Pubblicato il {new Date(upcoming.publish_at).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}
                    </p>
                  </div>

                  {cd && (
                    <div className="mt-5 grid grid-cols-3 gap-2 max-w-[320px]" data-testid="countdown">
                      <div className="bg-[#FF2768] text-white rounded-2xl p-3 text-center">
                        <p className="font-display italic text-3xl leading-none">{cd.days}</p>
                        <p className="text-[9px] uppercase tracking-widest mt-1 opacity-90">giorni</p>
                      </div>
                      <div className="bg-[#FFE45C] text-[#3D2140] rounded-2xl p-3 text-center">
                        <p className="font-display italic text-3xl leading-none">{cd.hours}</p>
                        <p className="text-[9px] uppercase tracking-widest mt-1 opacity-70">ore</p>
                      </div>
                      <div className="bg-[#81C3C9] text-white rounded-2xl p-3 text-center">
                        <p className="font-display italic text-3xl leading-none">{cd.minutes}</p>
                        <p className="text-[9px] uppercase tracking-widest mt-1 opacity-90">min</p>
                      </div>
                    </div>
                  )}
                  <p className="mt-6 text-[#3D2140]/70 leading-relaxed">
                    Il primo numero di <em>Crescere con Cura</em> arriverà nella tua casella se ti iscrivi alla newsletter <strong>Una lettera alla volta</strong> — così ti avviso appena è online.
                  </p>
                </div>
              </motion.div>
            </div>
          ) : (
            <div className="max-w-2xl mx-auto text-center">
              <Calendar size={48} className="mx-auto text-[#81C3C9] mb-4" />
              <p className="font-editorial italic text-2xl text-[#3D2140]">Il primo numero sta arrivando 🌿</p>
              <p className="mt-3 text-[#3D2140]/60">Iscriviti alla newsletter per essere avvisata alla pubblicazione.</p>
            </div>
          )}
        </section>
      )}

      {/* NEWSLETTER SIGNUP */}
      <NewsletterSignup />
    </div>
  );
}
